import React, {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	useTransition,
} from 'react';
import { fetchQuery, type RefetchFn, useRelayEnvironment } from 'react-relay';
import type { Subscription, GraphQLTaggedNode } from 'relay-runtime';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	HydrationRefetchQuery$variables,
	HydrationRefetchQuery,
	JiraJqlViewContext,
	JiraJqlScopeInput,
} from '@atlassian/jira-jql-builder/src/ui/__generated__/HydrationRefetchQuery.graphql';

type HydrationQueryContextProps = {
	/**
	 * Whether or not the hydration query is currently fetching
	 */
	hydrationQueryIsFetching: boolean;
	/**
	 * The JQL context for the last completed hydration operation
	 */
	hydrationQueryContextJql: string | undefined;
};

type HydrationQueryProviderProps = {
	refetch: RefetchFn<HydrationRefetchQuery>;
	query: GraphQLTaggedNode;
	contextJql: string;
	hydratedJql: string | undefined;
	children: ReactNode;
	viewContext?: JiraJqlViewContext | null;
	scope?: JiraJqlScopeInput | null;
};

const HydrationQueryContext = createContext<HydrationQueryContextProps>({
	hydrationQueryIsFetching: false,
	hydrationQueryContextJql: '',
});
HydrationQueryContext.displayName = 'HydrationQueryContext';

export const useHydrationQuery = () => useContext(HydrationQueryContext);
export const HydrationQueryProvider = ({
	refetch,
	query,
	contextJql,
	hydratedJql,
	children,
	viewContext,
	scope,
}: HydrationQueryProviderProps): React.JSX.Element => {
	const [hydrationQueryIsFetching, setHydrationQueryIsFetching] = useState(false);
	const cloudId = useCloudId();
	const atlassianAccountId = useAccountId();
	const environment = useRelayEnvironment();
	const inFlightRequest = useRef<Subscription>();
	const inFlightContextJql = useRef<string>();
	const [isPending, startTransition] = useTransition();

	useEffect(() => {
		const shouldRefetch = contextJql !== hydratedJql && contextJql !== inFlightContextJql.current;

		if (shouldRefetch) {
			// Prevent race condition from multiple requests in progress
			// (ex. if user clicks quickly on different filters)
			inFlightRequest.current?.unsubscribe();
			inFlightContextJql.current = contextJql;

			let variables: HydrationRefetchQuery$variables;

			if (fg('jira_nin_last_used_jql')) {
				variables = {
					filterAri: '',
					jql: '',
					input: {
						jql: contextJql,
					},
					accountId: atlassianAccountId ?? '',
					includeJira: true,
					includeUser: true,
					cloudId,
					isFilter: false,
					...(fg('jql_builder_add_view_context_to_hydration_query') && { viewContext }),
					scope,
					isPlanReleaseFeatureToggleEnabled: fg('simple-plans-m2-release-feature-toggle'),
				};
			} else {
				variables = {
					filterAri: '',
					jql: contextJql,
					input: null,
					accountId: atlassianAccountId ?? '',
					includeJira: true,
					includeUser: true,
					cloudId,
					isFilter: false,
					...(fg('jql_builder_add_view_context_to_hydration_query') && { viewContext }),
					scope,
					isPlanReleaseFeatureToggleEnabled: fg('simple-plans-m2-release-feature-toggle'),
				};
			}

			if (fg('jira_nin_use_transition_for_jql_hydration')) {
				startTransition(() => {
					// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
					refetch(variables, {
						fetchPolicy: 'store-or-network',
						onComplete: (error: Error | null) => {
							inFlightContextJql.current = undefined;
							if (error) {
								fireErrorAnalytics({
									meta: {
										id: 'hydrateJqlBuilderQuery',
										packageName: 'jiraJqlBuilder',
										teamName: 'empanada',
									},
									error,
									sendToPrivacyUnsafeSplunk: true,
								});
							}
						},
					});
				});
				return;
			}

			setHydrationQueryIsFetching(true);

			// Prefetch data, then update returned variables on complete
			inFlightRequest.current = fetchQuery<HydrationRefetchQuery>(environment, query, variables, {
				fetchPolicy: 'store-or-network',
				networkCacheConfig: {
					metadata: { META_SLOW_ENDPOINT: true },
				},
			}).subscribe({
				complete: () => {
					refetch(variables, {
						fetchPolicy: 'store-only',
						onComplete: () => {
							setHydrationQueryIsFetching(false);
							inFlightContextJql.current = undefined;
						},
					});
				},
				error: (error: Error) => {
					setHydrationQueryIsFetching(false);
					fireErrorAnalytics({
						meta: {
							id: 'hydrateJqlBuilderQuery',
							packageName: 'jiraJqlBuilder',
							teamName: 'empanada',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				},
			});
		}
	}, [
		atlassianAccountId,
		cloudId,
		contextJql,
		environment,
		hydratedJql,
		query,
		refetch,
		setHydrationQueryIsFetching,
		viewContext,
		scope,
	]);

	const contextValue: HydrationQueryContextProps = useMemo(
		() => ({
			hydrationQueryIsFetching: fg('jira_nin_use_transition_for_jql_hydration')
				? isPending
				: hydrationQueryIsFetching,
			hydrationQueryContextJql: hydratedJql,
		}),
		[hydrationQueryIsFetching, hydratedJql, isPending],
	);

	return (
		<HydrationQueryContext.Provider value={contextValue}>{children}</HydrationQueryContext.Provider>
	);
};
